import type { ApiErrorResponse, ApiListResponse, ApiSuccessResponse } from "@academia-hub/shared-types";
import { env } from "../env.js";
import { getAccessToken, isAuthRestriction, setRestriction, setUnauthenticated, updateAccessToken } from "../auth/auth-store.js";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly fields: ApiErrorResponse["error"]["fields"];

  constructor(status: number, body: ApiErrorResponse["error"]) {
    super(body.message);
    this.name = "ApiError";
    this.code = body.code;
    this.status = status;
    // An empty string (no body, no header) means "unknown" — keep it `undefined` so callers can test it.
    this.requestId = body.requestId || undefined;
    this.fields = body.fields;
  }
}

/** `ApiError.code` for a 5xx whose body isn't JSON — a proxy error page while the API restarts (KG-16). */
export const SERVER_UNAVAILABLE_CODE = "SERVER_UNAVAILABLE";

/** Shown for {@link SERVER_UNAVAILABLE_CODE}; `ServerUnavailableNotice` offers the retry. */
export const SERVER_UNAVAILABLE_MESSAGE = "The server is restarting or unavailable — try again in a moment.";

/**
 * True for a 5xx that didn't come from the API itself: a 502/503/504 HTML page (or empty body) from
 * nginx or a load balancer while the API is restarting. Retrying shortly usually succeeds.
 */
export function isServerUnavailable(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === SERVER_UNAVAILABLE_CODE;
}

/** Response header the API (and the edge proxy) echo the correlation ID in. */
const REQUEST_ID_HEADER = "X-Request-Id";

/**
 * A friendly message for an error response that carried no `{ error }` envelope — typically an
 * nginx HTML page (502/503/504) while the API is restarting, or a gateway timeout.
 */
function fallbackErrorMessage(status: number): string {
  if (status === 413) return "That upload is too large.";
  if (status === 429) return "Too many requests. Please wait a minute and try again.";
  if (status === 401) return "Your session has ended. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "The requested resource was not found.";
  return "The request could not be completed. Please try again.";
}

/** Parses a body as JSON, returning `undefined` for an empty or non-JSON body (never throws). */
function parseJsonSafely(text: string): unknown {
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function readErrorEnvelope(json: unknown): ApiErrorResponse["error"] | undefined {
  const error = (json as Partial<ApiErrorResponse> | undefined)?.error;
  return error && typeof error === "object" && typeof error.code === "string" && typeof error.message === "string" ? error : undefined;
}

/**
 * `ErrorState` props for a failed query/mutation: the request ID of an `ApiError`, so support can
 * find the server log line. Spread it: `<ErrorState description="..." {...errorStateProps(query.error)} />`.
 * With `fallbackDescription`, also sets `description` — the server's message for an `ApiError`, else the fallback.
 */
export function errorStateProps(error: unknown, fallbackDescription?: string): { requestId?: string; description?: string } {
  return {
    ...(error instanceof ApiError && error.requestId ? { requestId: error.requestId } : {}),
    ...(fallbackDescription !== undefined ? { description: error instanceof ApiError ? error.message : fallbackDescription } : {}),
    // A restarting server isn't the page's fault — say so instead of the page's own "could not load" copy.
    ...(isServerUnavailable(error) ? { description: SERVER_UNAVAILABLE_MESSAGE } : {}),
  };
}

/** Appends "(Request ID: …)" to a message when `error` is an `ApiError` that carries one. */
export function withRequestId(error: unknown, message: string): string {
  return error instanceof ApiError && error.requestId ? `${message} (Request ID: ${error.requestId})` : message;
}

/**
 * The common inline-error pattern — the server's message for an `ApiError`, else `fallback` — with
 * the request ID appended. Replaces `err instanceof ApiError ? err.message : fallback`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? withRequestId(error, error.message) : fallback;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

/** Error codes from `AuthGuard` (`apps/api/src/identity/guards/auth.guard.ts`) that mean "try a silent refresh once". */
const RETRYABLE_AUTH_CODES = new Set(["MISSING_ACCESS_TOKEN", "INVALID_ACCESS_TOKEN", "SESSION_REVOKED"]);

let refreshInFlight: Promise<boolean> | null = null;

/** Waits between refresh retries on a 5xx / network error (about 7 s in all — a container restart). */
export const REFRESH_RETRY_DELAYS_MS: readonly number[] = [1000, 2000, 4000];
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Calls `POST /auth/refresh` (cookie + CSRF-protected, ADR-0004) at most once
 * concurrently — several requests failing with 401 at the same moment must
 * not each trigger their own refresh race. `AuthProvider` also calls it first on page load, so the
 * bootstrap `GET /auth/me` never goes out without a token.
 */
export async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= withRefreshLock(async () => {
    try {
      // A 5xx or a dropped connection is the server restarting (a deploy), not a signed-out user: retry a
      // few times before giving up, or a deploy would send everyone mid-page-load to the login screen.
      // A 4xx (no/expired/reused cookie) is final at once.
      for (let attempt = 0; ; attempt++) {
        let response: Response;
        try {
          const csrfToken = readCookie("csrf_token");
          response = await fetch(`${env.VITE_API_BASE_URL}/auth/refresh`, {
            method: "POST",
            credentials: "include",
            headers: {
              ...(env.VITE_DEV_TENANT_SLUG ? { "X-Tenant-Slug": env.VITE_DEV_TENANT_SLUG } : {}),
              ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
            },
          });
        } catch {
          if (attempt >= REFRESH_RETRY_DELAYS_MS.length) return false;
          await sleep(REFRESH_RETRY_DELAYS_MS[attempt]!);
          continue;
        }
        if (response.status >= 500 && attempt < REFRESH_RETRY_DELAYS_MS.length) {
          await sleep(REFRESH_RETRY_DELAYS_MS[attempt]!);
          continue;
        }
        if (!response.ok) return false;
        const body = (await response.json()) as ApiSuccessResponse<{ accessToken: string }>;
        updateAccessToken(body.data.accessToken);
        return true;
      }
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  });
  return refreshInFlight;
}

/**
 * Tabs share the refresh cookie, and the API treats two simultaneous presentations of one refresh
 * token as reuse and revokes the family (ADR-0004), which would sign every tab out. The Web Locks
 * API queues refreshes across tabs, so the second tab sends the cookie the first one just rotated.
 * Where it's unavailable (old browsers, jsdom), the in-tab single flight above still applies.
 */
function withRefreshLock(run: () => Promise<boolean>): Promise<boolean> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks ? locks.request("academia-hub:auth-refresh", run) : run();
}

/**
 * Thin `fetch` wrapper: resolves the API base URL, attaches the dev-only tenant header (ADR-0003
 * — docs/decisions/0003-interim-tenant-resolution-before-auth.md; a no-op once the API is running
 * with `NODE_ENV=production`), attaches the in-memory access token and the CSRF double-submit
 * header (ADR-0004), and turns a non-2xx `{ error }` body (`00_CLAUDE_CODE_MASTER_PROMPT.md` §8)
 * into a thrown `ApiError`. On a 401 from an expired/invalid access token, attempts exactly one
 * silent refresh and retries the original request once — `07_UI_UX_SPEC.md` §24: "Expired
 * session: Attempt safe refresh once. If refresh fails, route to login."
 *
 * Returns the **raw parsed response body**, not auto-unwrapped — a single-resource endpoint's
 * body is `{ data: T }` and a list endpoint's is `{ data: T[], meta }`, so unwrapping "data"
 * unconditionally here would silently drop `meta` for every list call. `apiGetResource`/
 * `apiGetList` below type the call site correctly for each case.
 */
export async function apiFetch<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const response = await sendRequest(path, init, "application/json", isRetry);
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // A 2xx that isn't JSON is a misrouted request (e.g. the SPA's index.html served for an API path).
    throw new ApiError(response.status, {
      code: "INVALID_RESPONSE",
      message: "The server sent an unexpected response. Please try again.",
      requestId: response.headers.get(REQUEST_ID_HEADER) ?? "",
    });
  }
}

/** For the few non-JSON endpoints (CSV templates) — same auth, CSRF and refresh-once handling as `apiFetch`. */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const response = await sendRequest(path, { method: "GET" }, "*/*", false);
  return response.blob();
}

async function sendRequest(path: string, init: RequestInit | undefined, accept: string, isRetry: boolean): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", accept);
  // A `FormData` body (file upload) must NOT get an explicit Content-Type —
  // the browser sets `multipart/form-data; boundary=...` itself only when
  // the header is left unset.
  if (init?.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (env.VITE_DEV_TENANT_SLUG) {
    headers.set("X-Tenant-Slug", env.VITE_DEV_TENANT_SLUG);
  }
  const accessToken = getAccessToken();
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const csrfToken = readCookie("csrf_token");
  if (csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(`${env.VITE_API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (response.ok) {
    return response;
  }

  const text = await response.text().catch(() => "");
  const errorBody = readErrorEnvelope(parseJsonSafely(text));
  // A 5xx without the API's JSON envelope never reached the API (KG-16): a proxy page while it restarts.
  const code = errorBody?.code ?? (response.status >= 500 ? SERVER_UNAVAILABLE_CODE : "UNKNOWN_ERROR");
  const headerRequestId = response.headers.get(REQUEST_ID_HEADER) ?? "";

  if (!isRetry && response.status === 401 && RETRYABLE_AUTH_CODES.has(code) && path !== "/auth/refresh") {
    const refreshed = await refreshSession();
    if (refreshed) {
      return sendRequest(path, init, accept, true);
    }
    setUnauthenticated();
  }

  // ADR-0026 §15: the account must change its password or set up MFA first. Wherever this
  // surfaces, `RequireAuth` swaps the app for the matching forced screen.
  if (response.status === 403 && isAuthRestriction(code)) {
    setRestriction(code);
  }

  throw new ApiError(
    response.status,
    errorBody
      ? { ...errorBody, requestId: errorBody.requestId || headerRequestId }
      : { code, message: code === SERVER_UNAVAILABLE_CODE ? SERVER_UNAVAILABLE_MESSAGE : fallbackErrorMessage(response.status), requestId: headerRequestId },
  );
}

/** GET a single-resource endpoint (`{ data: T }`) and unwrap it to `T`. */
export async function apiGetResource<T>(path: string): Promise<T> {
  const body = await apiFetch<ApiSuccessResponse<T>>(path, { method: "GET" });
  return body.data;
}

/** GET a list endpoint and return the full `{ data, meta }` envelope (callers need `meta`). */
export function apiGetList<T>(path: string): Promise<ApiListResponse<T>> {
  return apiFetch<ApiListResponse<T>>(path, { method: "GET" });
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const result = await apiFetch<ApiSuccessResponse<T>>(path, {
    method: "POST",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return result.data;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const result = await apiFetch<ApiSuccessResponse<T>>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return result.data;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const result = await apiFetch<ApiSuccessResponse<T>>(path, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return result.data;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const result = await apiFetch<ApiSuccessResponse<T>>(path, { method: "DELETE" });
  return result.data;
}
